/**
 * Вторая волна библиотеки: идеи на индикаторах, которых раньше не было.
 *
 * Облако Ишимоку, параболик, Aroon, Williams %R, MFI, каналы Кельтнера и
 * Дончиана, пивоты, трёхсвечные фигуры — и связки, которых не хватало
 * целям студии: сжатие перед движением, возврат над VWAP, тренд по
 * «ленте» средних. Каждая собрана из того, что считает движок, у каждой
 * есть усилители и честный паспорт: когда работает и когда ломается.
 */
import { above, adx, alertOnly, bb, below, breakOut, close, crossDown, crossUp, ema, happened, ind, level, macdLine, macdSignal, mirror, number, previous, rsi, rvol, spec, vwap, weight, atr, divergenceParams, } from './kit.js';
const cloud = (output) => ind('ichimoku', { tenkan: 9, kijun: 26, senkou: 52 }, output);
const psar = ind('psar', { step: 0.02, max: 0.2 });
const aroon = (output) => ind('aroon', { period: 25 }, output);
const williams = ind('williams_r', { period: 14 });
const mfi = ind('mfi', { period: 14 });
const keltner = (output, period = 20) => ind('keltner', { period, multiplier: 2, atrPeriod: 10 }, output);
const donchian = (output, period = 20) => ind('donchian', { period }, output);
const pivot = (output) => ind('pivots', {}, output);
const cci = ind('cci', { period: 20 });
const obv = ind('obv', {});
/** Облако «в нашу сторону»: для покупок — над верхом, для продаж — под низом. */
const beyondCloud = (side) => side.beyond(close, cloud(side.sign > 0 ? 'cloud_top' : 'cloud_bottom'));
/** Линия канала «в нашу сторону». */
const edgeOf = (side, channel) => channel(side.sign > 0 ? 'upper' : 'lower');
const farEdgeOf = (side, channel) => channel(side.sign > 0 ? 'lower' : 'upper');
/** Флаг-фигура «в нашу сторону». */
const pattern = (id, side, params = {}) => happened(ind(id, params, side.way));
export const WAVE2_TEMPLATES = [
    /* ------------------------------------------------------------ Ишимоку */
    {
        id: 'ichimoku-breakout',
        name: 'Выход из облака Ишимоку',
        icon: '☁️',
        category: 'trend',
        hint: 'Цена вышла из облака, а короткая линия уже над длинной — тренд сменил режим.',
        steps: ['Тенкан по ту же сторону Киджуна', 'Цена вышла из облака'],
        keywords: ['ишимоку', 'ichimoku', 'облако', 'тренд', 'kumo'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '4H',
            idea: 'Облако — зона, где рынок не решил, куда идти. Выход из него с поддержкой коротких линий — решение принято.',
            why: 'Облако строится по прошлым диапазонам: выход за него значит, что цена ушла дальше обычных колебаний.',
            fails: 'В тонком облаке выходы частые и ложные; на младших свечах облако слишком нервное.',
            tip: 'Чем толще облако, из которого вышла цена, тем серьёзнее выход.',
        },
        build: (o) => spec(o, 'Выход из облака Ишимоку', mirror((side) => ({
            context: [side.beyond(cloud('tenkan'), cloud('kijun'))],
            trigger: side.cross(close, cloud(side.sign > 0 ? 'cloud_top' : 'cloud_bottom')),
            confirmations: [weight('c1', above(adx, number(20)), 60), weight('c2', above(rvol, number(1.2)), 40)],
        })), 0),
    },
    {
        id: 'ichimoku-tk',
        name: 'Тенкан–Киджун по облаку',
        icon: '⛩️',
        category: 'classic',
        hint: 'Классический вход Ишимоку: короткая линия пересекла длинную, а цена уже по нужную сторону облака.',
        steps: ['Цена по ту же сторону облака', 'Тенкан пересёк Киджун'],
        keywords: ['ишимоку', 'тенкан', 'киджун', 'tk cross', 'классика'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '4H',
            idea: 'Пересечение линий Ишимоку — как пересечение средних, но с фильтром: только по ту сторону облака, куда идёт тренд.',
            why: 'Фильтр облаком отбрасывает пересечения против большого движения — самые частые ложные.',
            fails: 'Цена долго внутри облака — сигналов почти нет, и это правильно.',
            tip: 'Японские трейдеры смотрели дневные свечи; на часах ставьте порог силы выше.',
        },
        build: (o) => spec(o, 'Тенкан–Киджун по облаку', mirror((side) => ({
            context: [beyondCloud(side)],
            trigger: side.cross(cloud('tenkan'), cloud('kijun')),
            confirmations: [weight('c1', side.beyond(close, ema(200)), 50), weight('c2', above(adx, number(20)), 50)],
        })), 0),
    },
    {
        id: 'ichimoku-pullback',
        name: 'Откат к облаку',
        icon: '🌥️',
        category: 'trend',
        hint: 'Цена над облаком, RSI откатился и снова пошёл вверх — откат внутри тренда закончился.',
        steps: ['Цена по ту же сторону облака', 'RSI вернулся через 45'],
        keywords: ['ишимоку', 'откат', 'pullback', 'rsi', 'тренд'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'Тренд есть (цена над облаком), цена немного отдохнула — RSI ушёл ниже 45 и вернулся. Отдых кончился.',
            why: 'Вход на откате в тренде дешевле погони за ценой: стоп короче, цель та же.',
            fails: 'Если откат превращается в разворот, цена войдёт в облако — это и есть выход.',
            tip: 'Стоп удобно ставить под верхом облака: туда цене возвращаться не положено.',
        },
        build: (o) => spec(o, 'Откат к облаку', mirror((side) => ({
            context: [beyondCloud(side)],
            trigger: side.cross(rsi, number(level(side, 45))),
            confirmations: [weight('c1', side.beyond(cloud('tenkan'), cloud('kijun')), 60), weight('c2', above(rvol, number(1.1)), 40)],
        })), 0),
    },
    /* ------------------------------------------------------------ параболик и Aroon */
    {
        id: 'psar-trend',
        name: 'Параболик по тренду',
        icon: '🎯',
        category: 'trend',
        hint: 'Точки параболика перескочили под цену, и это по ходу большого тренда.',
        steps: ['Цена по ту же сторону EMA 200', 'Параболик перевернулся'],
        keywords: ['параболик', 'psar', 'sar', 'тренд', 'стоп'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'Параболик — это подтягивающийся стоп. Когда цена его пробивает, он перескакивает на другую сторону — тренд сменился.',
            why: 'По ходу большого тренда переворот параболика ловит окончание отката.',
            fails: 'В боковике параболик переворачивается каждые пару свечей — фильтр EMA 200 спасает не всегда.',
            tip: 'Сами точки параболика — готовый стоп для бота: выход, когда цена их пересекла.',
        },
        build: (o) => spec(o, 'Параболик по тренду', mirror((side) => ({
            context: [side.beyond(close, ema(200))],
            trigger: side.cross(close, psar),
            confirmations: [weight('c1', above(adx, number(22)), 60), weight('c2', side.beyond(macdLine, macdSignal), 40)],
        })), 0),
    },
    {
        id: 'aroon-start',
        name: 'Начало тренда по Aroon',
        icon: '🌅',
        category: 'trend',
        hint: 'Свежие максимумы стали чаще свежих минимумов — у рынка появилось направление.',
        steps: ['Aroon «вверх» обогнал «вниз»', 'Тренд набирает силу'],
        keywords: ['aroon', 'арун', 'начало тренда', 'разворот'],
        passport: {
            level: 'skilled',
            market: 'any',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '4H',
            idea: 'Aroon считает, как давно был последний максимум и минимум. Когда максимумы становятся свежее минимумов — рынок пошёл вверх.',
            why: 'Индикатор смотрит на время, а не на цену: он раньше средних замечает, что рынок перестал падать.',
            fails: 'На коротком периоде линии путаются; 25 свечей — разумный минимум.',
            tip: 'Пара с ADX: Aroon говорит «куда», ADX — «насколько уверенно».',
        },
        build: (o) => spec(o, 'Начало тренда по Aroon', mirror((side) => ({
            context: [],
            trigger: side.cross(aroon('up'), aroon('down')),
            confirmations: [weight('c1', above(adx, number(20)), 50), weight('c2', side.beyond(close, ema(50)), 50)],
        })), 30),
    },
    {
        id: 'aroon-alert',
        name: 'Рынок повернул вверх (Aroon)',
        icon: '🧭',
        category: 'indicators',
        hint: 'Свежие максимумы стали чаще свежих минимумов — рынок повернул вверх. Предупреждение, а не вход.',
        steps: ['Разница Aroon перешла ноль вверх', 'Объём выше обычного'],
        keywords: ['aroon', 'направление', 'предупреждение'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'swing',
            style: 'alert',
            timeframe: '4H',
            idea: 'Когда разница Aroon становится больше нуля, свежие максимумы случаются чаще свежих минимумов.',
            why: 'Это повод посмотреть на график: падение, в котором вы ждали, могло кончиться.',
            fails: 'Не сигнал входа: смена знака бывает и в затяжном боковике.',
            tip: 'Удобно вешать на монеты из избранного, чтобы не следить за каждой.',
        },
        build: (o) => spec(o, 'Рынок повернул вверх (Aroon)', alertOnly({ context: [], trigger: crossUp(aroon('oscillator'), number(0)), confirmations: [weight('c1', above(rvol, number(1.2)), 100)] }), 0),
    },
    /* ------------------------------------------------------------ осцилляторы */
    {
        id: 'williams-trend',
        name: 'Williams %R по тренду',
        icon: '〽️',
        category: 'indicators',
        hint: 'Тренд вверх, %R вышел из перепроданности — откат закончился.',
        steps: ['Цена по ту же сторону EMA 100', 'Williams %R вышел из крайней зоны'],
        keywords: ['williams', '%r', 'уильямс', 'откат', 'тренд'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: '%R показывает, где закрытие внутри диапазона. Ниже −80 — у самого дна; возврат выше — откат выдохся.',
            why: 'В тренде провалы к дну диапазона короткие, а возврат из них — дешёвый вход.',
            fails: 'Против тренда та же картина — ловля ножа. Поэтому EMA 100 обязательна.',
            tip: 'Ларри Уильямс брал период 10 — на часах это быстрее и шумнее.',
        },
        build: (o) => spec(o, 'Williams %R по тренду', mirror((side) => ({
            context: [side.beyond(close, ema(100))],
            trigger: side.cross(williams, number(level(side, -80, -50))),
            confirmations: [weight('c1', side.beyond(close, ema(200)), 60), weight('c2', above(adx, number(20)), 40)],
        })), 0),
    },
    {
        id: 'williams-extreme',
        name: 'Williams %R: разворот у края',
        icon: '🪃',
        category: 'reversal',
        hint: '%R долго сидел у края и вышел оттуда — рынок развернулся.',
        steps: ['Williams %R вышел из крайней зоны', 'RSI тоже у края'],
        keywords: ['williams', '%r', 'разворот', 'перепроданность'],
        passport: {
            level: 'skilled',
            market: 'range',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '1H',
            idea: 'Цена закрывалась у самого дна диапазона, а теперь поднялась — продавцы выдохлись.',
            why: 'В коридоре цена ходит от края к краю: выход %R из крайней зоны — начало хода к другому краю.',
            fails: 'В сильном тренде %R сидит у края неделями, и каждый «выход» — ловушка.',
            tip: 'Хорошо в паре с «Тренда нет (ADX ниже 20)» — добавьте его фоном.',
        },
        build: (o) => spec(o, 'Williams %R: разворот у края', mirror((side) => ({
            context: [below(adx, number(25))],
            trigger: side.cross(williams, number(level(side, -80, -50))),
            confirmations: [weight('c1', side.sign > 0 ? below(rsi, number(40)) : above(rsi, number(60)), 60), weight('c2', above(rvol, number(1.2)), 40)],
        })), 0),
    },
    {
        id: 'cci-trend',
        name: 'CCI: возвращение в тренд',
        icon: '🔁',
        category: 'indicators',
        hint: 'Тренд вверх, CCI провалился ниже −100 и вернулся — откат закончен.',
        steps: ['Цена по ту же сторону EMA 50', 'CCI вернулся из-за ±100'],
        keywords: ['cci', 'откат', 'тренд', 'ламберт'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'CCI меряет, как далеко цена ушла от средней. Провал за −100 в растущем рынке — глубокий откат; возврат — его конец.',
            why: 'Откаты в тренде редко глубже обычного: вернувшийся CCI показывает, что покупатель снова у руля.',
            fails: 'Если CCI не возвращается долго, тренд, скорее всего, уже другой.',
            tip: 'CCI ± 100 — договорённость, а не закон: попробуйте ±150 в «Устойчивости».',
        },
        build: (o) => spec(o, 'CCI: возвращение в тренд', mirror((side) => ({
            context: [side.beyond(close, ema(50))],
            trigger: side.cross(cci, number(side.sign > 0 ? -100 : 100)),
            confirmations: [weight('c1', side.beyond(ema(50), ema(200)), 60), weight('c2', above(rvol, number(1.1)), 40)],
        })), 0),
    },
    {
        id: 'rsi-50-trend',
        name: 'RSI через 50 по тренду',
        icon: '⚖️',
        category: 'indicators',
        hint: 'RSI перешёл середину в сторону крупного тренда — импульс снова на стороне тренда.',
        steps: ['Цена по ту же сторону EMA 200', 'RSI пересёк 50'],
        keywords: ['rsi', '50', 'середина', 'тренд', 'импульс'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'RSI выше 50 — покупатели сильнее продавцов. Переход через середину по ходу тренда — откат кончился.',
            why: 'Середина RSI проще краёв: до неё цена доходит часто, а сигнал по тренду надёжнее разворотного.',
            fails: 'Без силы тренда RSI пилит 50 туда-сюда.',
            tip: 'Самая простая идея для первого бота: понятно, почему вошёл, и легко проверить.',
        },
        build: (o) => spec(o, 'RSI через 50 по тренду', mirror((side) => ({
            context: [side.beyond(close, ema(200))],
            trigger: side.cross(rsi, number(50)),
            confirmations: [weight('c1', above(adx, number(20)), 60), weight('c2', above(rvol, number(1.2)), 40)],
        })), 30),
    },
    /* ------------------------------------------------------------ объём и деньги */
    {
        id: 'mfi-reversal',
        name: 'MFI: деньги возвращаются',
        icon: '💸',
        category: 'volume',
        hint: 'MFI был ниже 20 и вернулся — деньги снова заходят в монету.',
        steps: ['MFI вернулся из крайней зоны', 'Свеча в нашу сторону'],
        keywords: ['mfi', 'money flow', 'деньги', 'объём', 'разворот'],
        passport: {
            level: 'skilled',
            market: 'any',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '1H',
            idea: 'MFI — это RSI, взвешенный объёмом. Ниже 20 — деньги уходили; возврат выше — пошли обратно.',
            why: 'Объём честнее цены: вернувшиеся деньги чаще разворачивают рынок, чем просто дешёвая цена.',
            fails: 'На тонких монетах объём рваный, и MFI прыгает сам по себе.',
            tip: 'Сильнее всего у уровня: пивота, прошлой впадины, нижней полосы.',
        },
        build: (o) => spec(o, 'MFI: деньги возвращаются', mirror((side) => ({
            context: [],
            trigger: side.cross(mfi, number(level(side, 20))),
            confirmations: [weight('c1', side.beyond(close, ind('ema', { period: 9 })), 50), weight('c2', above(rvol, number(1.3)), 50)],
        })), 40),
    },
    {
        id: 'mfi-heat',
        name: 'Денежный перегрев',
        icon: '🌡️',
        category: 'volume',
        hint: 'MFI выше 85: денег зашло слишком много и слишком быстро. Предупреждение.',
        steps: ['MFI пересёк 85 вверх', 'Объём выше обычного'],
        keywords: ['mfi', 'перегрев', 'деньги', 'предупреждение'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'day',
            style: 'alert',
            timeframe: '1H',
            idea: 'Почти весь объём последних свечей пришёлся на рост. Так выглядит толпа, которая боится опоздать.',
            why: 'Когда купили все, кто хотел, покупать становится некому — после такого обычно пауза.',
            fails: 'На новостях перегрев может длиться дольше, чем кажется разумным.',
            tip: 'Повод подтянуть стоп у открытой покупки, а не открыть продажу.',
        },
        build: (o) => spec(o, 'Денежный перегрев', alertOnly({ context: [], trigger: crossUp(mfi, number(85)), confirmations: [weight('c1', above(rvol, number(1.3)), 100)] }), 0),
    },
    {
        id: 'obv-breakout',
        name: 'Пробой, подтверждённый OBV',
        icon: '📶',
        category: 'volume',
        hint: 'Цена пробила экстремум 20 свечей, а накопленный объём растёт вместе с ней.',
        steps: ['OBV растёт за 20 свечей', 'Пробит экстремум 20 свечей'],
        keywords: ['obv', 'объём', 'пробой', 'грэнвилл'],
        passport: {
            level: 'skilled',
            market: 'any',
            horizon: 'swing',
            style: 'breakout',
            timeframe: '4H',
            idea: 'OBV складывает объём со знаком: растёт — деньги заходят. Пробой цены вместе с ростом OBV — пробой с деньгами.',
            why: 'Пробой без денег — колебание. Пробой, за которым стоит накопленный объём, — решение участников.',
            fails: 'Один гигантский бар объёма может развернуть OBV — смотрите на график.',
            tip: 'Джо Грэнвилл говорил: объём идёт впереди цены. Здесь — проверка этого правила.',
        },
        build: (o) => spec(o, 'Пробой, подтверждённый OBV', mirror((side) => ({
            context: [side.beyond(obv, previous(obv, 20))],
            trigger: breakOut(side, 20),
            confirmations: [weight('c1', above(rvol, number(1.5)), 60), weight('c2', side.beyond(close, ema(50)), 40)],
        })), 0),
    },
    {
        id: 'vwap-volume-reclaim',
        name: 'Возврат над VWAP на объёме',
        icon: '🧲',
        category: 'scalping',
        hint: 'Цена вернулась выше средней цены с учётом объёма — покупатели забрали контроль.',
        steps: ['Цена пересекла VWAP', 'Объём выше обычного'],
        keywords: ['vwap', 'скальпинг', 'внутри дня', 'возврат'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'scalp',
            style: 'with-trend',
            timeframe: '15m',
            idea: 'VWAP — средняя цена, по которой торговали сегодня. Над ней покупатели в плюсе, под ней — в минусе.',
            why: 'Возврат над VWAP на объёме значит, что продавцы не удержали цену, а покупатели вошли.',
            fails: 'Около VWAP цена часто пилит — нужен объём, иначе это шум.',
            tip: 'Цель — ближайший экстремум дня, стоп — под VWAP: просто и наглядно.',
        },
        build: (o) => spec(o, 'Возврат над VWAP на объёме', mirror((side) => ({
            context: [],
            trigger: side.cross(close, vwap),
            confirmations: [weight('c1', above(rvol, number(1.3)), 60), weight('c2', side.beyond(close, ema(50)), 40)],
        })), 50),
    },
    /* ------------------------------------------------------------ каналы и сжатие */
    {
        id: 'keltner-breakout',
        name: 'Пробой канала Кельтнера',
        icon: '🛤️',
        category: 'breakout',
        hint: 'Закрытие за линией Кельтнера — движение сильнее обычного хода свечей.',
        steps: ['Закрытие за каналом Кельтнера', 'Объём выше обычного'],
        keywords: ['кельтнер', 'keltner', 'канал', 'пробой', 'atr'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'day',
            style: 'breakout',
            timeframe: '1H',
            idea: 'Канал Кельтнера — средняя плюс-минус два средних хода свечи. За него цена выходит, только когда движение необычно сильное.',
            why: 'Канал меряет движение в ходах свечи, а не в процентах — пробой одинаково значим на спокойной и бурной монете.',
            fails: 'После долгого роста пробой бывает последним рывком перед разворотом.',
            tip: 'Возврат внутрь канала — естественный выход для бота.',
        },
        build: (o) => spec(o, 'Пробой канала Кельтнера', mirror((side) => ({
            context: [],
            trigger: side.cross(close, edgeOf(side, keltner)),
            confirmations: [weight('c1', above(rvol, number(1.4)), 60), weight('c2', above(adx, number(20)), 40)],
        })), 40),
    },
    {
        id: 'keltner-reversion',
        name: 'Возврат в канал Кельтнера',
        icon: '🎾',
        category: 'range',
        hint: 'В спокойном рынке цена выскочила за нижнюю линию и вернулась — вероятен ход к середине.',
        steps: ['Тренда нет (ADX ниже 22)', 'Цена вернулась внутрь канала'],
        keywords: ['кельтнер', 'боковик', 'возврат к средней', 'mean reversion'],
        passport: {
            level: 'skilled',
            market: 'range',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '1H',
            idea: 'В боковике выход за канал — перебор. Когда цена возвращается обратно, она обычно идёт к середине.',
            why: 'Без тренда крайние цены не держатся: их некому поддержать.',
            fails: 'Начало тренда выглядит так же, только цена не возвращается. Фильтр ADX защищает частично.',
            tip: 'Цель — средняя линия канала; дальше идея уже не обещает ничего.',
        },
        build: (o) => spec(o, 'Возврат в канал Кельтнера', mirror((side) => ({
            context: [below(adx, number(22))],
            trigger: side.cross(close, farEdgeOf(side, keltner)),
            confirmations: [weight('c1', side.sign > 0 ? below(rsi, number(45)) : above(rsi, number(55)), 60), weight('c2', above(rvol, number(1.1)), 40)],
        })), 0),
    },
    {
        id: 'keltner-trend-ride',
        name: 'Откат к середине Кельтнера',
        icon: '🏄',
        category: 'trend',
        hint: 'Тренд вверх, цена откатилась к средней линии канала Кельтнера и снова пошла вверх.',
        steps: ['EMA 20 по ту же сторону EMA 50', 'Цена вернулась через середину канала'],
        keywords: ['кельтнер', 'откат', 'тренд', 'середина канала'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'В тренде цена гуляет между серединой канала и его краем. Касание середины и возврат — конец отката.',
            why: 'Середина канала Кельтнера — это EMA 20: в здоровом тренде откаты к ней покупают.',
            fails: 'Если цена ушла к дальнему краю канала, тренд, скорее всего, сломан.',
            tip: 'Цель — ближний край канала, стоп — за дальним.',
        },
        build: (o) => spec(o, 'Откат к середине Кельтнера', mirror((side) => ({
            context: [side.beyond(ema(20), ema(50))],
            trigger: side.cross(close, keltner('middle')),
            confirmations: [weight('c1', above(adx, number(20)), 60), weight('c2', side.beyond(close, ema(200)), 40)],
        })), 30),
    },
    {
        id: 'donchian-scalp',
        name: 'Быстрый Дончиан',
        icon: '🏃',
        category: 'scalping',
        hint: 'Пробой короткого канала из 10 свечей по ходу EMA 50 — частые входы на младших свечах.',
        steps: ['Цена по ту же сторону EMA 50', 'Закрытие за каналом Дончиана 10'],
        keywords: ['дончиан', 'скальпинг', 'пробой', 'быстрый'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'scalp',
            style: 'breakout',
            timeframe: '5m',
            idea: 'Короткий канал — короткие пробои. По ходу средней их продолжение вероятнее.',
            why: 'На младших свечах тренды короткие, и короткий канал успевает их поймать.',
            fails: 'Комиссия съедает мелкие движения: без цели и стопа идея проигрывает на издержках.',
            tip: 'Проверьте в «Устойчивости» период канала: 10 и 12 должны давать похожие числа.',
        },
        build: (o) => spec(o, 'Быстрый Дончиан', mirror((side) => ({
            context: [side.beyond(close, ema(50))],
            trigger: side.cross(close, edgeOf(side, (output) => donchian(output, 10))),
            confirmations: [weight('c1', above(rvol, number(1.3)), 60), weight('c2', above(adx, number(20)), 40)],
        })), 30),
    },
    {
        id: 'aroon-pullback',
        name: 'Откат при свежих максимумах',
        icon: '🪁',
        category: 'trend',
        hint: 'Aroon показывает свежие максимумы, а RSI откатился и вернулся выше 40 — продолжение тренда.',
        steps: ['Aroon «вверх» выше 70', 'RSI вернулся через 40'],
        keywords: ['aroon', 'откат', 'rsi', 'тренд'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'Максимумы обновлялись совсем недавно — тренд жив. Короткий откат увёл RSI ниже 40, и он вернулся.',
            why: 'Aroon отвечает «тренд ещё свежий?», RSI — «откат кончился?». Вместе — вход в живой тренд на откате.',
            fails: 'Если Aroon падает ниже 50, максимумы старые — тренд выдыхается, и идея молчит не зря.',
            tip: 'Хорошая пара к «Началу тренда по Aroon»: тот ловит старт, этот — продолжение.',
        },
        build: (o) => spec(o, 'Откат при свежих максимумах', mirror((side) => ({
            context: [above(aroon(side.sign > 0 ? 'up' : 'down'), number(70))],
            trigger: side.cross(rsi, number(level(side, 40))),
            confirmations: [weight('c1', side.beyond(close, ema(100)), 60), weight('c2', above(rvol, number(1.1)), 40)],
        })), 30),
    },
    {
        id: 'calm-breakout',
        name: 'Пробой после затишья',
        icon: '🌋',
        category: 'breakout',
        hint: 'Рынок спал — ATR ниже обычного, — и вдруг цена пробила экстремум 20 свечей.',
        steps: ['ATR(14) ниже ATR(50)', 'Пробит экстремум 20 свечей'],
        keywords: ['затишье', 'atr', 'пробой', 'волатильность'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'day',
            style: 'breakout',
            timeframe: '1H',
            idea: 'Свечи были мельче обычного — рынок спал. Пробой из такого состояния часто открывает новое движение.',
            why: 'Спокойный рынок накапливает заявки за краями коридора; пробой их срабатывает разом.',
            fails: 'В выходные затишье — норма, и пробои там слабые.',
            tip: 'Стоп за серединой коридора: если цена туда вернулась, пробоя не было.',
        },
        build: (o) => spec(o, 'Пробой после затишья', mirror((side) => ({
            context: [below(atr(14), atr(50))],
            trigger: breakOut(side, 20),
            confirmations: [weight('c1', above(rvol, number(1.5)), 70), weight('c2', side.beyond(close, ema(50)), 30)],
        })), 0),
    },
    {
        id: 'mfi-panic',
        name: 'Деньги бегут',
        icon: '🏃‍♂️',
        category: 'volume',
        hint: 'MFI ниже 15: почти весь объём последних свечей пришёлся на падение. Предупреждение.',
        steps: ['MFI пересёк 15 вниз', 'Объём выше обычного'],
        keywords: ['mfi', 'паника', 'деньги', 'предупреждение'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'day',
            style: 'alert',
            timeframe: '1H',
            idea: 'Из монеты уходят деньги, и уходят быстро: продают на объёме, не торгуясь.',
            why: 'Такая распродажа выдыхается, когда продать больше некому, — часто рядом с этим местом бывает дно.',
            fails: 'На плохих новостях бегство длится дольше одного дня — это повод смотреть, а не покупать.',
            tip: 'Пара к «Денежному перегреву»: вместе они показывают обе крайности толпы.',
        },
        build: (o) => spec(o, 'Деньги бегут', alertOnly({ context: [], trigger: crossDown(mfi, number(15)), confirmations: [weight('c1', above(rvol, number(1.3)), 100)] }), 0),
    },
    /* ------------------------------------------------------------ уровни */
    {
        id: 'pivot-bounce',
        name: 'Отскок от пивота',
        icon: '🏓',
        category: 'price-action',
        hint: 'Цена провалилась под первую поддержку пивотов и вернулась — уровень устоял.',
        steps: ['Цена вернулась над S1 (под R1)', 'RSI не перегрет'],
        keywords: ['пивот', 'pivot', 'поддержка', 'сопротивление', 'отскок'],
        passport: {
            level: 'novice',
            market: 'range',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '15m',
            idea: 'Пивоты считаются из прошлых суток, и их видит весь рынок. Возврат над поддержкой — уровень удержали.',
            why: 'Уровни, которые знают все, работают как договорённость: от них покупают и продают.',
            fails: 'В трендовый день уровни пробиваются один за другим.',
            tip: 'Цель — центральный пивот: до него цена доходит чаще, чем до противоположного уровня.',
        },
        build: (o) => spec(o, 'Отскок от пивота', mirror((side) => ({
            context: [],
            trigger: side.cross(close, pivot(side.sign > 0 ? 's1' : 'r1')),
            confirmations: [weight('c1', side.sign > 0 ? below(rsi, number(50)) : above(rsi, number(50)), 50), weight('c2', above(rvol, number(1.2)), 50)],
        })), 40),
    },
    {
        id: 'pivot-breakout',
        name: 'Пробой сопротивления R1',
        icon: '🧱',
        category: 'breakout',
        hint: 'Цена пробила первое сопротивление пивотов на объёме — день трендовый.',
        steps: ['Цена выше центрального пивота', 'Пробит R1 (S1)'],
        keywords: ['пивот', 'pivot', 'пробой', 'внутри дня'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'day',
            style: 'breakout',
            timeframe: '15m',
            idea: 'Когда цена уходит за первый уровень пивотов, день перестаёт быть боковым.',
            why: 'За R1 стоят стопы продавцов, игравших от уровня: их выбивание ускоряет движение.',
            fails: 'Ложные пробои R1 — обычное дело в тихие дни; объём обязателен.',
            tip: 'Цель — R2, стоп — под центральным пивотом.',
        },
        build: (o) => spec(o, 'Пробой сопротивления R1', mirror((side) => ({
            context: [side.beyond(close, pivot('pp'))],
            trigger: side.cross(close, pivot(side.sign > 0 ? 'r1' : 's1')),
            confirmations: [weight('c1', above(rvol, number(1.4)), 70), weight('c2', above(adx, number(20)), 30)],
        })), 40),
    },
    {
        id: 'pivot-alert',
        name: 'Цена у центрального пивота',
        icon: '📍',
        category: 'price-action',
        hint: 'Цена пересекла центральный уровень прошлых суток. Предупреждение без стороны.',
        steps: ['Цена пересекла пивот'],
        keywords: ['пивот', 'уровень', 'предупреждение'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'day',
            style: 'alert',
            timeframe: '15m',
            idea: 'Центральный пивот — «справедливая» цена прошлого дня. Пересечение — смена настроения внутри дня.',
            why: 'Многие боты и трейдеры смотрят на этот уровень, поэтому реакция у него частая.',
            fails: 'В спокойный день цена крутится вокруг пивота, и оповещений много.',
            tip: 'Удобно, чтобы не сидеть у графика: оповещение придёт само.',
        },
        build: (o) => spec(o, 'Цена у центрального пивота', alertOnly({ context: [], trigger: crossUp(close, pivot('pp')), confirmations: [weight('c1', above(rvol, number(1.2)), 100)] }), 0),
    },
    /* ------------------------------------------------------------ свечи */
    {
        id: 'star-reversal',
        name: 'Утренняя звезда',
        icon: '⭐',
        category: 'price-action',
        hint: 'Три свечи разворота после снижения: сильная красная, маленькая, сильная зелёная.',
        steps: ['Цена ниже средней Боллинджера', 'Сложилась утренняя (вечерняя) звезда'],
        keywords: ['звезда', 'morning star', 'свечи', 'разворот'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '4H',
            idea: 'Продавцы давили, потом рынок замер на маленькой свече, потом покупатели забрали больше половины падения.',
            why: 'Фигура показывает смену власти за три свечи — это медленнее поглощения, зато нагляднее.',
            fails: 'Посреди коридора звезда ничего не значит — нужна предыдущая просадка.',
            tip: 'Сильнее всего у уровня: нижней полосы Боллинджера, пивота, прошлой впадины.',
        },
        build: (o) => spec(o, 'Утренняя звезда', mirror((side) => ({
            context: [side.sign > 0 ? below(close, bb('middle')) : above(close, bb('middle'))],
            trigger: pattern('star', side),
            confirmations: [weight('c1', above(rvol, number(1.2)), 50), weight('c2', side.sign > 0 ? below(rsi, number(45)) : above(rsi, number(55)), 50)],
        })), 0),
    },
    {
        id: 'three-soldiers',
        name: 'Три солдата на объёме',
        icon: '💂',
        category: 'price-action',
        hint: 'Три сильные свечи подряд в одну сторону, и объём выше обычного — ход набран.',
        steps: ['Три солдата (три вороны)', 'Объём выше обычного'],
        keywords: ['три солдата', 'три вороны', 'свечи', 'продолжение'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'Три свечи подряд закрылись дальше предыдущей и открылись внутри её тела — движение уверенное.',
            why: 'Одна сильная свеча — случай, три подряд — намерение. Объём подтверждает, что за ним деньги.',
            fails: 'После долгого движения три солдата бывают последним рывком — проверьте RSI.',
            tip: 'Не догоняйте: дождитесь небольшого отката к третьей свече.',
        },
        build: (o) => spec(o, 'Три солдата на объёме', mirror((side) => ({
            context: [],
            trigger: pattern('three_soldiers', side),
            confirmations: [weight('c1', above(rvol, number(1.2)), 60), weight('c2', side.beyond(close, ema(50)), 40)],
        })), 0),
    },
    /* ------------------------------------------------------------ связки */
    {
        id: 'ema-ribbon',
        name: 'Лента средних',
        icon: '🎗️',
        category: 'trend',
        hint: 'EMA 9, 21 и 50 выстроены по порядку, и цена вернулась над самой быстрой — тренд продолжился.',
        steps: ['EMA 21 по ту же сторону EMA 50', 'Цена пересекла EMA 9'],
        keywords: ['ema', 'лента', 'ribbon', 'тренд', 'средние'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'Когда средние лежат по порядку — быстрая над медленной, — тренд здоров. Короткий нырок под EMA 9 и возврат — вход.',
            why: 'Порядок средних — самая наглядная картина тренда; вход у быстрой средней — короткий стоп.',
            fails: 'Когда средние сплетаются, тренда нет — сигналов будет много и пустых.',
            tip: 'Выход для бота: закрытие за EMA 21.',
        },
        build: (o) => spec(o, 'Лента средних', mirror((side) => ({
            context: [side.beyond(ema(21), ema(50))],
            trigger: side.cross(close, ema(9)),
            confirmations: [weight('c1', side.beyond(ema(50), ema(200)), 50), weight('c2', above(adx, number(20)), 50)],
        })), 50),
    },
    {
        id: 'macd-psar',
        name: 'MACD и параболик',
        icon: '🧩',
        category: 'indicators',
        hint: 'MACD пересёк сигнальную, а параболик уже на нужной стороне — импульс и тренд согласны.',
        steps: ['Цена по ту же сторону параболика', 'MACD пересёк сигнальную'],
        keywords: ['macd', 'параболик', 'psar', 'связка'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'Две независимые проверки: параболик говорит про тренд, MACD — про импульс. Вход, когда они согласны.',
            why: 'Пересечения MACD против параболика — самые частые ложные; фильтр их убирает.',
            fails: 'Оба индикатора запаздывают — на коротких движениях вход будет поздним.',
            tip: 'Стоп — точка параболика: она сама подтянется за ценой.',
        },
        build: (o) => spec(o, 'MACD и параболик', mirror((side) => ({
            context: [side.beyond(close, psar)],
            trigger: side.cross(macdLine, macdSignal),
            confirmations: [weight('c1', side.beyond(close, ema(100)), 60), weight('c2', above(rvol, number(1.1)), 40)],
        })), 0),
    },
    {
        id: 'hidden-divergence-trend',
        name: 'Скрытая дивергенция по тренду',
        icon: '🫥',
        category: 'smart-money',
        hint: 'В восходящем тренде цена сделала более высокую впадину, а RSI — более низкую: откат закончился.',
        steps: ['Цена по ту же сторону EMA 100', 'Скрытая дивергенция'],
        keywords: ['дивергенция', 'скрытая', 'hidden divergence', 'тренд', 'rsi'],
        passport: {
            level: 'pro',
            market: 'trend',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '4H',
            idea: 'Обычная дивергенция ловит разворот, скрытая — продолжение: импульс на откате упал сильнее, чем цена.',
            why: 'Цена держится выше прошлой впадины, хотя импульс провалился — значит, продавцы слабы.',
            fails: 'Сигналы редкие; на коротком периоде RSI скрытых дивергенций много и они шумные.',
            tip: 'Сочетайте с «Возвратом в разрыв»: скрытая дивергенция у FVG — классика.',
        },
        build: (o) => spec(o, 'Скрытая дивергенция по тренду', mirror((side) => ({
            context: [side.beyond(close, ema(100))],
            trigger: happened(ind('divergence', divergenceParams, side.sign > 0 ? 'hidden_up' : 'hidden_down')),
            confirmations: [weight('c1', side.beyond(ema(50), ema(200)), 60), weight('c2', above(rvol, number(1.1)), 40)],
        })), 0),
    },
    {
        id: 'cloud-mfi',
        name: 'Облако и деньги',
        icon: '🌦️',
        category: 'volume',
        hint: 'Цена над облаком Ишимоку, MFI пересёк 50 — тренд и деньги на одной стороне.',
        steps: ['Цена по ту же сторону облака', 'MFI пересёк 50'],
        keywords: ['ишимоку', 'mfi', 'деньги', 'тренд'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'Облако показывает режим рынка, MFI — куда идут деньги. Когда они совпали, сигнал крепче.',
            why: 'Объём подтверждает то, что цена показывает формой: тренд с деньгами живёт дольше.',
            fails: 'Внутри облака идея молчит; на тонких монетах MFI шумит.',
            tip: 'Стоп удобно ставить за дальним краем облака.',
        },
        build: (o) => spec(o, 'Облако и деньги', mirror((side) => ({
            context: [beyondCloud(side)],
            trigger: side.cross(mfi, number(50)),
            confirmations: [weight('c1', side.beyond(cloud('tenkan'), cloud('kijun')), 60), weight('c2', above(rvol, number(1.2)), 40)],
        })), 0),
    },
    {
        id: 'aroon-donchian',
        name: 'Aroon и пробой канала',
        icon: '🛫',
        category: 'breakout',
        hint: 'Пробой канала Дончиана, когда Aroon уже показывает свежие экстремумы в ту же сторону.',
        steps: ['Aroon в нашу сторону выше 70', 'Закрытие за каналом Дончиана 20'],
        keywords: ['aroon', 'дончиан', 'пробой', 'тренд'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'swing',
            style: 'breakout',
            timeframe: '4H',
            idea: 'Aroon выше 70 — экстремумы в нашу сторону свежие, тренд жив. Пробой канала в этот момент — продолжение.',
            why: 'Пробой по ходу свежего тренда проходит чаще, чем пробой из ниоткуда.',
            fails: 'Поздние пробои в конце длинного тренда — частый источник убытков.',
            tip: 'Сравните в «Дуэли» с «Черепахами · 20»: фильтр Aroon обычно убирает треть сигналов.',
        },
        build: (o) => spec(o, 'Aroon и пробой канала', mirror((side) => ({
            context: [above(aroon(side.sign > 0 ? 'up' : 'down'), number(70))],
            trigger: side.cross(close, edgeOf(side, donchian)),
            confirmations: [weight('c1', above(rvol, number(1.3)), 60), weight('c2', above(adx, number(20)), 40)],
        })), 0),
    },
];
//# sourceMappingURL=wave2.js.map